/**
 * Role-based permission utilities
 * 
 * This file contains utility functions for managing role-based permissions
 * and feature access in the application.
 */

import { useSelector } from 'react-redux';
import { selectActiveRole } from '../slices/userRoleSlice';

// Define permissions for each role
const rolePermissions = {
  admin: [
    'createBook',
    'editBook',
    'deleteBook',
    'importBook',
    'assignVoiceActor',
    'manageUsers',
    'manageVoiceActors',
    'recordVoice',
    'editVoiceProfile',
    'viewAnalytics',
    'systemSettings'
  ],
  bookCreator: [
    'createBook',
    'editBook',
    'deleteBook',
    'importBook',
    'assignVoiceActor',
    'trackRecordingProgress'
  ],
  voiceActor: [
    'recordVoice',
    'editVoiceProfile',
    'viewAssignments',
    'submitRecordings'
  ]
};

// Define features available to each role
const roleFeatures = {
  admin: [
    'bookDetails',
    'textLibrary',
    'voiceActors',
    'voiceLab',
    'voiceLabMyRecordings',
    'voiceLibrary',
    'audioLibrary',
    'storyImporter',
    'aiFileLab',
    'voiceRecorderLab',
    'adminArticleEditor',
    'adminAiQueryEditor',
    'userManagement',
    'bookCreatorWorksheet',
    'voiceActorWorksheetEnh',
    'mySampleVoices',
    'VoiceSampleWorksheet',
    'workInProgress',
    'messagingTest',
    // Phase 4d Step 3 — Rendering Studio R1 (admin sees it for support).
    'renderingStudio'
  ],
  bookCreator: [
    'bookDetails',
    'textLibrary',
    'voiceActors',
    'audioLibrary',
    'storyImporter',
    'aiFileLab',
    'voiceActorAssignments',
    'bookCreatorWorksheet',
    // Phase 4d Step 3 — Rendering Studio R1. Eye-test the cloned grid before
    // committing to scene-level rendering.
    'renderingStudio'
  ],
  voiceActor: [
    'myAssignments',
    'voiceLab',
    'voiceLabMyRecordings',
    'myPortfolio',
    'voiceActorWorksheetEnh',
    'mySampleVoices',
    'VoiceSampleWorksheet',
    'workInProgress'
  ]
};

/**
 * Check if a user with the given role has a specific permission
 * @param {string} role - The user's role
 * @param {string} permission - The permission to check
 * @returns {boolean} - Whether the user has the permission
 */
export const hasPermission = (role, permission) => {
  // Admin has all permissions
  if (role === 'admin') return true;

  // Check if the role exists and has the permission
  return rolePermissions[role]?.includes(permission) || false;
};

/**
 * Get all available features for a given role
 * @param {string} role - The user's role
 * @returns {string[]} - Array of feature identifiers
 */
export const getAvailableFeatures = (role) => {
  return roleFeatures[role] || [];
};

/**
 * Check if a feature is available for a given role
 * @param {string} role - The user's role
 * @param {string} feature - The feature identifier
 * @returns {boolean} - Whether the feature is available
 */
export const hasFeatureAccess = (role, feature) => {
  // Admin has access to all features
  if (role === 'admin') return true;

  // Check if the role exists and has access to the feature
  return roleFeatures[role]?.includes(feature) || false;
};

/**
 * Get the menu ID for a feature
 * @param {string} feature - The feature identifier
 * @returns {string} - The menu ID for the feature
 */
export const getMenuIdForFeature = (feature) => {
  const menuIdMap = {
    'bookDetails': 'BOOK-DETAILS',
    'textLibrary': 'TEXT-LIBRARY',
    'voiceActors': 'VOICE-ACTORS',
    'voiceActorsOpportunities': 'VOICE-ACTOR-OPPORTUNITIES',
    'voiceLab': 'VOICE-LAB',
    'voiceLabMyRecordings': 'VOICE-LAB-MY-RECORDINGS',
    'voiceLibrary': 'VOICE-LIBRARY',
    'audioLibrary': 'AUDIO-LIBRARY',
    'storyImporter': 'STORY-IMPORTER',
    'aiFileLab': 'AI-FILE-LAB',
    // 'voiceRecorderLab': 'VOICE-RECORDER-LAB',
    'adminArticleEditor': 'ADMIN-ARTICLE-EDITOR',
    'adminAiQueryEditor': 'ADMIN-AI-QUERY-EDITOR',
    'userManagement': 'USER-MANAGEMENT',
    'myAssignments': 'MY-ASSIGNMENTS',
    'myPortfolio': 'MY-PORTFOLIO',
    'voiceActorAssignments': 'VOICE-ACTOR-ASSIGNMENTS',
    'bookCreatorWorksheet': 'BOOK-CREATOR-WORKSHEET',
    // 'voiceActorWorksheet': 'VOICE-ACTOR-WORKSHEET',
    'voiceActorWorksheetEnh': 'VOICE-ACTOR-WORKSHEET-ENH',
    'mySampleVoices': 'MY-SAMPLE-VOICES',
    'VoiceSampleWorksheet': 'VOICE-SAMPLE-WORKSHEET',
    'workInProgress': 'WORK-IN-PROGRESS',
    'messagingTest': 'MESSAGING-TEST',
    'renderingStudio': 'RENDERING-STUDIO'
  };

  return menuIdMap[feature] || feature.toUpperCase().replace(/\s+/g, '-');
};

/**
 * Custom hook to check if the current user has access to a feature
 * @param {string} feature - The feature to check
 * @returns {boolean} - Whether the user has access to the feature
 */
export const useHasFeatureAccess = (feature) => {
  const activeRole = useSelector(selectActiveRole) || 'admin';
  return hasFeatureAccess(activeRole, feature);
};
